@if (siteContentService.faqLoading()) {
    <div class="text-center text-muted-color py-6">Loading...</div>
} @else {
    <p-table
        #faqDataTable
        [value]="siteContentService.faqItems()"
        dataKey="Id"
        [rows]="10"
        [paginator]="true"
        [showGridlines]="true"
        [globalFilterFields]="['Title', 'Category', 'Message']"
        responsiveLayout="scroll">
        <ng-template #caption>
            <div class="flex justify-between items-center gap-2">
                <div class="flex gap-2">
                    <p-button label="New" icon="pi pi-plus" severity="success" (click)="openAddDialog()"></p-button>
                    <p-button label="Import" icon="pi pi-download" severity="secondary" outlined (click)="openImportDialog()"></p-button>
                </div>
                <p-iconfield iconPosition="left">
                    <p-inputicon>
                        <i class="pi pi-search"></i>
                    </p-inputicon>
                    <input pInputText type="text" (input)="onGlobalFilter(faqDataTable, $event)" placeholder="Search..." />
                </p-iconfield>
            </div>
        </ng-template>
        <ng-template pTemplate="header">
            <tr>
                <th pSortableColumn="Category" style="min-width: 8rem">Category <p-sortIcon field="Category" /></th>
                <th pSortableColumn="Title" style="min-width: 12rem">Title <p-sortIcon field="Title" /></th>
                <th pSortableColumn="Message" style="min-width: 12rem">Message <p-sortIcon field="Message" /></th>
                <th pSortableColumn="DisplayOrder" style="min-width: 6rem">Order <p-sortIcon field="DisplayOrder" /></th>
                <th>Actions</th>
            </tr>
        </ng-template>
        <ng-template pTemplate="body" let-item>
            <tr>
                <td>
                    <p-tag [value]="item.Category" severity="info"></p-tag>
                </td>
                <td>{{ item.Title }}</td>
                <td class="text-sm">{{ item.Message | slice:0:80 }}{{ item.Message?.length > 80 ? '...' : '' }}</td>
                <td>{{ item.DisplayOrder ?? '—' }}</td>
                <td>
                    <div class="flex items-center gap-2">
                        <p-button icon="pi pi-pencil" severity="warn" [rounded]="true" [outlined]="true" pTooltip="Edit" (click)="openEditDialog(item.Id)"></p-button>
                    </div>
                </td>
            </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
            <tr>
                <td colspan="5" class="text-center text-muted-color py-6">No FAQ items found.</td>
            </tr>
        </ng-template>
    </p-table>
}

<!-- CRUD dialog -->
<p-dialog
    [header]="dialogMode() === 'add' ? 'New FAQ Item' : 'Edit FAQ Item'"
    [visible]="dialogVisible()"
    (visibleChange)="dialogVisible.set($event)"
    [breakpoints]="{ '960px': '75vw' }"
    [style]="{ width: '50vw' }"
    [modal]="true"
>
    <p-fluid>
        <div class="flex flex-col gap-4">
            <div>
                <label class="block font-medium mb-1">Category</label>
                <input pInputText class="w-full" [ngModel]="editCategory()" (ngModelChange)="editCategory.set($event ?? '')" />
            </div>
            <div>
                <label class="block font-medium mb-1">Title</label>
                <input pInputText class="w-full" [ngModel]="editTitle()" (ngModelChange)="editTitle.set($event ?? '')" />
            </div>
            <div>
                <label class="block font-medium mb-1">Message</label>
                <textarea pTextarea class="w-full" [rows]="8" [ngModel]="editMessage()" (ngModelChange)="editMessage.set($event ?? '')"></textarea>
            </div>
            <div>
                <label class="block font-medium mb-1">Display Order</label>
                <p-inputNumber class="w-full" [ngModel]="editDisplayOrder()" (ngModelChange)="editDisplayOrder.set($event)" [showButtons]="true" [allowEmpty]="true"></p-inputNumber>
            </div>
            @if (saveError()) {
                <div class="text-red-500 text-sm">{{ saveError() }}</div>
            }
        </div>
    </p-fluid>
    <ng-template #footer>
        <div class="flex justify-end gap-2">
            <p-button label="Cancel" severity="secondary" (click)="dialogVisible.set(false)"></p-button>
            <p-button label="Save" icon="pi pi-check" [loading]="saving()" (click)="saveItem()"></p-button>
        </div>
    </ng-template>
</p-dialog>

<p-toast />

<!-- Import dialog -->
<p-dialog
    header="Import FAQ from Another Website"
    [visible]="importDialogVisible()"
    (visibleChange)="importDialogVisible.set($event)"
    [breakpoints]="{ '960px': '90vw' }"
    [style]="{ width: '75vw' }"
    [modal]="true"
    [closable]="!importing()"
>
    <div class="mb-4">
        <label class="block font-medium mb-1">Source Website</label>
        <p-select
            class="w-full max-w-xs"
            [ngModel]="importSourceRealmId()"
            (ngModelChange)="onImportRealmChange($event)"
            [options]="importableRealms()"
            optionLabel="label"
            optionValue="value"
            placeholder="Select source website…"
            [loading]="realmService.loading()"
            [disabled]="importing()"
            appendTo="body"
        />
    </div>

    @if (importSourceRealmId()) {
        <p-table
            [value]="importSourceItems()"
            dataKey="Id"
            selectionMode="multiple"
            [(selection)]="importSelectedItems"
            [loading]="importSourceLoading()"
            [rows]="10"
            [paginator]="true"
            [showGridlines]="true"
            responsiveLayout="scroll"
        >
            <ng-template #caption>
                <div class="text-sm text-muted-color">
                    @if (importSelectedItems.length) {
                        {{ importSelectedItems.length }} item(s) selected
                    } @else {
                        Select items to import
                    }
                </div>
            </ng-template>
            <ng-template pTemplate="header">
                <tr>
                    <th style="width: 3rem">
                        <p-tableHeaderCheckbox />
                    </th>
                    <th style="min-width: 8rem">Category</th>
                    <th style="min-width: 14rem">Title</th>
                    <th style="min-width: 7rem">Status</th>
                    <th style="width: 5rem"></th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-item>
                <tr>
                    <td>
                        <p-tableCheckbox [value]="item" />
                    </td>
                    <td><p-tag [value]="item.Category" severity="info" /></td>
                    <td>{{ item.Title }}</td>
                    <td>
                        @if (getImportStatus(item) === 'override') {
                            <p-tag value="Override" severity="warn" icon="pi pi-refresh" [rounded]="true" />
                        } @else {
                            <p-tag value="New" severity="success" icon="pi pi-plus" [rounded]="true" />
                        }
                    </td>
                    <td>
                        @if (getImportStatus(item) === 'override') {
                            <p-button icon="pi pi-code" severity="help" [rounded]="true" [outlined]="true" pTooltip="Compare" tooltipPosition="left" (click)="openCompareDialog(item)" />
                        } @else {
                            <p-button icon="pi pi-eye" severity="info" [rounded]="true" [outlined]="true" pTooltip="Preview" tooltipPosition="left" (click)="openPreviewDialog(item)" />
                        }
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="5" class="text-center text-muted-color py-6">
                        @if (importSourceLoading()) { Loading… }
                        @else { No FAQ items found for this website. }
                    </td>
                </tr>
            </ng-template>
        </p-table>
    }

    <ng-template #footer>
        <p-button
            label="Cancel"
            text
            severity="secondary"
            (click)="importDialogVisible.set(false)"
            [disabled]="importing()"
        />
        <p-button
            label="Import Selected"
            icon="pi pi-download"
            [disabled]="!importSelectedItems.length || importing()"
            [loading]="importing()"
            (click)="executeImport()"
        />
    </ng-template>
</p-dialog>

<!-- Preview dialog -->
<p-dialog
    header="FAQ Preview"
    [visible]="previewDialogVisible()"
    (visibleChange)="previewDialogVisible.set($event)"
    [breakpoints]="{ '960px': '80vw' }"
    [style]="{ width: '50vw' }"
    [modal]="true"
>
    @if (previewItem()) {
        <div class="flex flex-col gap-4">
            <div class="flex items-center gap-2">
                <span class="font-medium text-muted-color">Category:</span>
                <p-tag [value]="previewItem()!.Category" severity="info" />
            </div>
            <div>
                <span class="font-medium text-muted-color">Title:</span>
                <span class="ml-2 font-medium">{{ previewItem()!.Title }}</span>
            </div>
            @if (previewItem()!.DisplayOrder !== null) {
                <div>
                    <span class="font-medium text-muted-color">Display Order:</span>
                    <span class="ml-2">{{ previewItem()!.DisplayOrder }}</span>
                </div>
            }
            <div>
                <span class="font-medium text-muted-color block mb-1">Message:</span>
                <p-scrollpanel [style]="{ width: '100%', height: '20rem' }">
                    <pre class="app-code"><code class="whitespace-pre-wrap break-words">{{ previewItem()!.Message }}</code></pre>
                </p-scrollpanel>
            </div>
        </div>
    }
    <ng-template #footer>
        <p-button label="Close" text severity="secondary" (click)="previewDialogVisible.set(false)" />
    </ng-template>
</p-dialog>

<!-- Compare dialog -->
<p-dialog
    header="Compare FAQ Item"
    [visible]="compareDialogVisible()"
    (visibleChange)="compareDialogVisible.set($event)"
    [breakpoints]="{ '960px': '95vw' }"
    [style]="{ width: '85vw' }"
    [modal]="true"
>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-0 divide-x divide-surface">
        <!-- Origin -->
        <div class="pr-4">
            <div class="flex items-center gap-2 mb-4">
                <p-tag value="Origin" severity="secondary" />
                <span class="text-muted-color text-sm">current website</span>
            </div>
            @if (compareOriginItem()) {
                <div class="flex flex-col gap-3">
                    <div class="flex items-center gap-2">
                        <span class="font-medium text-muted-color text-sm">Category</span>
                        <p-tag [value]="compareOriginItem()!.Category" severity="info" />
                    </div>
                    <div>
                        <span class="font-medium text-muted-color text-sm">Title</span>
                        <p class="mt-0.5 m-0">{{ compareOriginItem()!.Title }}</p>
                    </div>
                    @if (compareOriginItem()!.DisplayOrder !== null) {
                        <div>
                            <span class="font-medium text-muted-color text-sm">Display Order</span>
                            <p class="mt-0.5 m-0">{{ compareOriginItem()!.DisplayOrder }}</p>
                        </div>
                    }
                    <div>
                        <span class="font-medium text-muted-color text-sm block mb-1">Message</span>
                        <p-scrollpanel [style]="{ width: '100%', height: '18rem' }">
                            <pre class="app-code"><code class="whitespace-pre-wrap break-words">{{ compareOriginItem()!.Message }}</code></pre>
                        </p-scrollpanel>
                    </div>
                </div>
            } @else {
                <div class="text-muted-color text-sm py-4">Not found.</div>
            }
        </div>
        <!-- New -->
        <div class="pl-4">
            <div class="flex items-center gap-2 mb-4">
                <p-tag value="New" severity="warn" />
                <span class="text-muted-color text-sm">source website</span>
            </div>
            @if (compareNewItem()) {
                <div class="flex flex-col gap-3">
                    <div class="flex items-center gap-2">
                        <span class="font-medium text-muted-color text-sm">Category</span>
                        <p-tag [value]="compareNewItem()!.Category" severity="info" />
                    </div>
                    <div>
                        <span class="font-medium text-muted-color text-sm">Title</span>
                        <p class="mt-0.5 m-0">{{ compareNewItem()!.Title }}</p>
                    </div>
                    @if (compareNewItem()!.DisplayOrder !== null) {
                        <div>
                            <span class="font-medium text-muted-color text-sm">Display Order</span>
                            <p class="mt-0.5 m-0">{{ compareNewItem()!.DisplayOrder }}</p>
                        </div>
                    }
                    <div>
                        <span class="font-medium text-muted-color text-sm block mb-1">Message</span>
                        <p-scrollpanel [style]="{ width: '100%', height: '18rem' }">
                            <pre class="app-code"><code class="whitespace-pre-wrap break-words">{{ compareNewItem()!.Message }}</code></pre>
                        </p-scrollpanel>
                    </div>
                </div>
            } @else {
                <div class="text-muted-color text-sm py-4">Not found.</div>
            }
        </div>
    </div>
    <ng-template #footer>
        <p-button label="Close" text severity="secondary" (click)="compareDialogVisible.set(false)" />
    </ng-template>
</p-dialog>
